import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Vollbild-Foto-Galerie im Airbnb-Stil: Pfeile, Miniaturbilder unten, Klick-Zoom,
 * Tastatur- und Wisch-Navigation. Wird per <PhotoLightbox images={[...]} index={n} onClose={...} /> geöffnet.
 */
export function PhotoLightbox({ images, index, onClose, title = "Foto" }) {
  const [current, setCurrent] = useState(index);
  const [zoomed, setZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState("center center");
  const touchStartX = useRef(null);
  const thumbRefs = useRef([]);

  useEffect(() => { setCurrent(index); setZoomed(false); }, [index]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    thumbRefs.current[current]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [current]);

  const go = (dir) => {
    setZoomed(false);
    setCurrent((c) => (c + dir + images.length) % images.length);
  };

  const onImageClick = (e) => {
    if (!zoomed) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomOrigin(`${x}% ${y}%`);
    }
    setZoomed((z) => !z);
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null || zoomed) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 60) go(-1);
    else if (delta < -60) go(1);
    touchStartX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[var(--ink)]/97 flex flex-col fade-in" onClick={onClose}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 text-white shrink-0" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm text-white/60">{current + 1} / {images.length}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoomed((z) => !z)} aria-label={zoomed ? "Herauszoomen" : "Hineinzoomen"}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
            {zoomed ? <ZoomOut size={18} /> : <ZoomIn size={18} />}
          </button>
          <button onClick={onClose} aria-label="Schließen"
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden px-2" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => go(-1)} aria-label="Vorheriges Foto"
          className="hidden sm:flex absolute left-4 z-10 w-11 h-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
          <ChevronLeft size={22} />
        </button>

        <div
          className="w-full h-full flex items-center justify-center overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <img
            key={current}
            src={images[current]}
            alt={`${title} – ${current + 1} von ${images.length}`}
            onClick={onImageClick}
            className={`max-w-full max-h-full object-contain select-none scale-in ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
            style={{
              transform: zoomed ? "scale(2.2)" : "scale(1)",
              transformOrigin: zoomOrigin,
              transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
            }}
            draggable={false}
          />
        </div>

        <button onClick={() => go(1)} aria-label="Nächstes Foto"
          className="hidden sm:flex absolute right-4 z-10 w-11 h-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Thumbnail strip */}
      <div className="shrink-0 px-4 py-4 overflow-x-auto hide-scrollbar" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-2 justify-start sm:justify-center min-w-min mx-auto">
          {images.map((src, i) => (
            <button
              key={i}
              ref={(el) => (thumbRefs.current[i] = el)}
              onClick={() => { setZoomed(false); setCurrent(i); }}
              aria-label={`Foto ${i + 1} anzeigen`}
              aria-current={i === current ? "true" : undefined}
              className={`shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                i === current ? "border-[var(--terra)] opacity-100" : "border-transparent opacity-50 hover:opacity-80"}`}
            >
              <img src={src} alt={`${title} – Miniaturbild ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
